import { useEffect, useState } from "react";
import { X, Sparkles, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STORAGE_KEY = "sig-shine-welcome-shown";

export default function WelcomePopup() {
    const [visible, setVisible] = useState(false);
    const [email, setEmail] = useState("");
    const [submitted, setSubmitted] = useState(null); // voucher object once minted
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        // Only trigger on the homepage, only once per browser
        if (window.location.pathname !== "/") return;
        if (localStorage.getItem(STORAGE_KEY)) return;

        // Fire on exit-intent (mouse leaves top) OR after 20s dwell — whichever first
        let done = false;
        const trigger = () => {
            if (done) return;
            done = true;
            setVisible(true);
            localStorage.setItem(STORAGE_KEY, "1");
        };
        const onLeave = (e) => { if (e.clientY <= 0) trigger(); };
        document.addEventListener("mouseleave", onLeave);
        const timer = setTimeout(trigger, 20000);
        return () => {
            document.removeEventListener("mouseleave", onLeave);
            clearTimeout(timer);
        };
    }, []);

    if (!visible) return null;

    const claim = async (e) => {
        e.preventDefault();
        if (!/\S+@\S+\.\S+/.test(email)) {
            toast.error("Please enter a valid email.");
            return;
        }
        setBusy(true);
        try {
            const r = await api.post("/welcome-voucher", { email });
            setSubmitted(r.data.voucher);
            toast.success("Welcome discount unlocked!");
        } catch {
            toast.error("Something went wrong — please try again.");
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(submitted.code);
            toast.success("Code copied.");
        } catch { /* noop */ }
    };

    return (
        <div
            data-testid="welcome-popup"
            className="fixed inset-0 z-50 flex items-center justify-center bg-navy/70 backdrop-blur-sm px-4"
            onClick={() => setVisible(false)}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="relative bg-cream w-full max-w-md border border-gold shadow-2xl p-8 md:p-10"
            >
                <button
                    onClick={() => setVisible(false)}
                    data-testid="welcome-close"
                    aria-label="Close"
                    className="absolute top-4 right-4 text-navy/60 hover:text-navy"
                >
                    <X className="w-5 h-5" />
                </button>

                {!submitted ? (
                    <>
                        <Sparkles className="w-8 h-8 text-gold-dark mb-3" />
                        <span className="uppercase tracking-[0.3em] text-[10px] font-bold text-gold-dark">— Welcome offer</span>
                        <h2 className="font-playfair text-3xl text-navy mt-2 mb-3">Wait — <span className="italic text-gold-dark">€15 off</span> your first clean.</h2>
                        <p className="font-outfit text-sm text-muted-foreground mb-6">
                            Leave your email and we&apos;ll send you a one-time voucher code you can use at checkout. No spam, unsubscribe any time.
                        </p>
                        <form onSubmit={claim} className="flex flex-col gap-3">
                            <input
                                data-testid="welcome-email"
                                type="email"
                                required
                                placeholder="you@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="border border-cream-border bg-white px-4 py-3 font-outfit text-navy focus:outline-none focus:border-gold"
                            />
                            <button
                                type="submit"
                                data-testid="welcome-submit"
                                disabled={busy}
                                className="bg-navy text-gold border border-gold px-6 py-3 font-outfit text-sm uppercase tracking-wider hover:bg-gold hover:text-navy transition-colors disabled:opacity-50"
                            >
                                {busy ? "Unlocking…" : "Get €15 off"}
                            </button>
                        </form>
                        <p className="text-[10px] font-outfit text-muted-foreground/70 mt-4">
                            One voucher per email. Not stackable with referral discounts.
                        </p>
                    </>
                ) : (
                    <div data-testid="welcome-success">
                        <Sparkles className="w-8 h-8 text-gold-dark mb-3" />
                        <h2 className="font-playfair text-3xl text-navy mb-2">You&apos;re in.</h2>
                        <p className="font-outfit text-sm text-muted-foreground mb-6">
                            Use this code at checkout — €15 comes off your total.
                        </p>
                        <div className="bg-navy text-cream border border-gold px-5 py-4 flex items-center justify-between mb-4">
                            <span className="font-mono tracking-[0.3em] text-gold" data-testid="welcome-code">{submitted.code}</span>
                            <button onClick={copy} data-testid="welcome-copy" className="inline-flex items-center gap-2 text-gold hover:text-cream text-xs uppercase tracking-wider">
                                <Copy className="w-3.5 h-3.5" /> Copy
                            </button>
                        </div>
                        <a
                            href={`/book?voucher=${submitted.code}`}
                            className="block text-center bg-gold text-navy px-6 py-3 font-outfit font-semibold text-sm uppercase tracking-wider hover:bg-gold-light transition-colors"
                            data-testid="welcome-book-now"
                        >
                            Book now with €15 off
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}
